import {useTheme} from 'styled-components';

import {useRef, useState, useEffect, useCallback, RefObject} from 'react';

/** ID of the DMS main scroll container — defined in apps/dms AppContainer. */
const APP_CONTAINER_ID = 'dms-app-container';

interface UseSidebarHeightReturn {
  ref: RefObject<HTMLDivElement | null>;
  height: string;
}

/**
 * Computes the correct height for the sticky activity sidebar panel.
 *
 * The sidebar uses `position: sticky; top: 0` inside `#dms-app-container`.
 * On initial load (before scrolling), the sidebar sits below the entity header,
 * so a fixed `calc(100dvh - 48px)` height would extend beyond the viewport bottom.
 *
 * This hook measures the sidebar element's actual top position in the viewport
 * and computes: height = viewportHeight - max(sidebarTopInViewport, 0)
 *
 * When sticky (scrolled past entity header): sidebarTop = 0 (relative to app container)
 *   → height = viewportHeight - topBarHeight  (fills viewport below TopBar)
 *
 * When in-flow (entity header visible): sidebarTop > topBarHeight
 *   → height = viewportHeight - sidebarTop  (fits within visible area)
 */
export function useSidebarHeight(): UseSidebarHeightReturn {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  const topBarHeight = theme.getSize(12); // 12 theme units × 4px = 48px

  const [height, setHeight] = useState<string>(`calc(100dvh - ${topBarHeight})`);

  const recalculate = useCallback(() => {
    if (!ref.current) {
      return;
    }

    const rect = ref.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    // rect.top is the sidebar's current top edge in the viewport.
    // Clamp to 0 so we never produce a negative height.
    const effectiveTop = Math.max(rect.top, 0);
    const available = Math.max(0, viewportHeight - effectiveTop);

    setHeight(`${available}px`);
  }, []);

  const scheduleRecalculate = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(recalculate);
  }, [recalculate]);

  useEffect(
    function subscribeToLayoutChanges() {
      recalculate();

      const scrollContainer = document.getElementById(APP_CONTAINER_ID);

      const handleScroll = () => scheduleRecalculate();
      const handleResize = () => scheduleRecalculate();

      window.addEventListener('resize', handleResize);
      scrollContainer?.addEventListener('scroll', handleScroll, {passive: true});

      let resizeObserver: ResizeObserver | null = null;

      if (ref.current) {
        resizeObserver = new ResizeObserver(scheduleRecalculate);
        resizeObserver.observe(ref.current);

        if (scrollContainer) {
          // Observe the scroll container's direct child (the page VStack) —
          // its height grows as page content loads asynchronously, triggering
          // recalculation when the page becomes scrollable.
          const scrollContent = scrollContainer.firstElementChild;
          if (scrollContent) {
            resizeObserver.observe(scrollContent);
          }
        }
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        scrollContainer?.removeEventListener('scroll', handleScroll);
        resizeObserver?.disconnect();

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    },
    [recalculate, scheduleRecalculate]
  );

  return {ref, height};
}
